// Global keyboard shortcuts:
//  - g then <key> navigates: h=home, t=tenants, p=players, a=audit,
//    r=rounds-explorer, w=wallet-ledger, s=system-health, d=redis-monitoring,
//    o=round-ops, u=admin-users, x=reports, e=approvals
//  - j / k moves selection in any focusable .content table (rows highlighted
//    with .row-active class); Enter clicks the first link or button in the row
//
// All shortcuts are no-ops while typing in an input/textarea/contenteditable.

import { navigate } from './nav.js';

const NAV_TARGETS: Record<string, string> = {
  h: '/home',
  t: '/integrators',
  p: '/players',
  a: '/audit',
  r: '/rounds-explorer',
  w: '/wallet-ledger',
  s: '/system-health',
  // d for datastore — s is already system-health.
  d: '/redis-monitoring',
  o: '/round-ops',
  u: '/admin-users',
  x: '/reports',
  e: '/approvals',
};

function isTextTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function activeTable(): HTMLTableElement | null {
  // Find the first non-hidden table inside #content. j/k operates on its tbody.
  const content = document.getElementById('content');
  if (!content) return null;
  const tables = Array.from(content.querySelectorAll<HTMLTableElement>('table'));
  for (const t of tables) {
    if (t.offsetParent !== null) return t;
  }
  return null;
}

function moveSelection(delta: 1 | -1): void {
  const table = activeTable();
  if (!table) return;
  const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr'));
  if (rows.length === 0) return;
  let i = rows.findIndex((r) => r.classList.contains('row-active'));
  if (i < 0) i = delta > 0 ? -1 : rows.length;
  i = Math.min(rows.length - 1, Math.max(0, i + delta));
  rows.forEach((r) => r.classList.remove('row-active'));
  const target = rows[i];
  if (target) {
    target.classList.add('row-active');
    target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

function activateRow(): void {
  const row = document.querySelector<HTMLTableRowElement>('tr.row-active');
  if (!row) return;
  const link = row.querySelector<HTMLAnchorElement>('a[href]');
  if (link) {
    link.click();
    return;
  }
  const btn = row.querySelector<HTMLButtonElement>('button');
  if (btn) btn.click();
}

export function installShortcuts(): void {
  let waitingForNav: number | null = null;

  window.addEventListener('keydown', (ev) => {
    if (isTextTarget(ev.target)) return;
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;

    const key = ev.key.toLowerCase();

    if (waitingForNav !== null) {
      window.clearTimeout(waitingForNav);
      waitingForNav = null;
      const target = NAV_TARGETS[key];
      if (target) {
        ev.preventDefault();
        navigate(target);
      }
      return;
    }

    if (key === 'g') {
      ev.preventDefault();
      waitingForNav = window.setTimeout(() => {
        waitingForNav = null;
      }, 1500);
      return;
    }

    if (key === 'j') {
      ev.preventDefault();
      moveSelection(1);
      return;
    }

    if (key === 'k') {
      ev.preventDefault();
      moveSelection(-1);
      return;
    }

    if (key === 'enter') {
      const row = document.querySelector('tr.row-active');
      if (row) {
        ev.preventDefault();
        activateRow();
      }
    }
  });
}
